import React from 'react';
import { useIntl } from 'react-intl';
import { gql, useQuery } from '@apollo/client';
import { formatCurrency as fmtCur, currencySymbol } from '../../../utils/formatters';

const GET_OPEN_PARTIAL_SALES_MINI = gql`
  query GetOpenPartialSalesMini($limit: Int) {
    getOpenPartialSales(limit: $limit) {
      id
      segment_details
      number_of_clients
      assets_under_management
      fee_based_revenue
      trails_revenue
      direct_revenue
      commission_revenue
      states
    }
    openPartialSalesCount
    openPartialSalesNewThisWeekCount
  }
`;

const formatCurrency = (value) => {
  const n = Number(value || 0);
  const sym = currencySymbol();
  if (n >= 1_000_000_000) return `${sym}${(n / 1_000_000_000).toFixed(1).replace(/\.0$/, '')}B`;
  if (n >= 1_000_000) return `${sym}${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  if (n >= 1_000) return `${sym}${Math.round(n / 1_000)}K`;
  return fmtCur(n);
};

const PartialSalesMiniList = ({ showHeader = true }) => {
  const intl = useIntl();
  const { data, loading, error } = useQuery(GET_OPEN_PARTIAL_SALES_MINI, { variables: { limit: 3 }, fetchPolicy: 'network-only' });
  const listings = Array.isArray(data?.getOpenPartialSales) ? data.getOpenPartialSales.slice(0, 3) : [];
  const totalActive = Number(data?.openPartialSalesCount || 0);
  const newThisWeek = Number(data?.openPartialSalesNewThisWeekCount || 0);

  return (
    <div className="mt-1 flex-1 flex flex-col">
      {showHeader && (
        <div className="mb-3">
          <h3>{intl.formatMessage({ id: 'succession.partial_sales.mini_list.header_title' })}</h3>
          <h4>{intl.formatMessage({ id: 'succession.partial_sales.mini_list.active_listings' }, { count: totalActive })}</h4>
          <p className="text-sm text-succession-success">{intl.formatMessage({ id: 'succession.partial_sales.mini_list.new_this_week' }, { count: newThisWeek })}</p>
        </div>
      )}
      {loading ? (
        <div className="mt-4 space-y-3">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="h-16 rounded bg-succession-bg-secondary animate-pulse border border-succession-border" />
          ))}
        </div>
      ) : error ? (
        <div className="mt-4 text-succession-error text-sm">{intl.formatMessage({ id: 'succession.partial_sales.mini_list.error_loading' })}</div>
      ) : listings.length === 0 ? (
        <div className="mt-4 text-succession-text-secondary text-sm">{intl.formatMessage({ id: 'succession.partial_sales.mini_list.no_listings' })}</div>
      ) : (
        <div className="space-y-3">
          {listings.map((l) => {
            const statesText = (l.states || []).join(', ') || '—';
            const production = Number(l.fee_based_revenue || 0) + Number(l.trails_revenue || 0) + Number(l.direct_revenue || 0) + Number(l.commission_revenue || 0);
            return (
              <div key={l.id} className="border border-succession-border rounded-lg px-3 py-3 bg-succession-bg-primary">
                <div className="grid grid-cols-12 items-center gap-3">
                  {/* Col 1: Icon */}
                  <div className="col-span-1 flex items-center justify-center">
                    <div className="w-9 h-9 rounded-lg bg-succession-success-bg flex items-center justify-center">
                      <svg className="w-5 h-5 text-succession-success" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                        <circle cx="12" cy="12" r="9" />
                        <path d="M12 12 L12 3 A9 9 0 0 1 21 12 Z" fill="currentColor" stroke="none" />
                      </svg>
                    </div>
                  </div>
                  {/* Col 2: Title + Location */}
                  <div className="col-span-5 min-w-0">
                    <div className="text-sm font-semibold text-succession-text-primary truncate" title={l.segment_details || intl.formatMessage({ id: 'succession.partial_sales.mini_list.practice_segment' })}>
                      {l.segment_details || intl.formatMessage({ id: 'succession.partial_sales.mini_list.practice_segment' })}
                    </div>
                    <div className="text-xs text-succession-text-secondary truncate" title={statesText}>{statesText}</div>
                  </div>
                  {/* Col 3: AUM */}
                  <div className="col-span-2 text-right">
                    <div className="text-xs text-succession-text-secondary mb-0.5">
                      <span className="inline-flex items-center gap-1 justify-end">
                        <svg className="w-3 h-3 text-blue-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M12 8c-1.7 0-3 .9-3 2s1.3 2 3 2 3 .9 3 2-1.3 2-3 2" />
                        </svg>
                        <span>{intl.formatMessage({ id: 'succession.partial_sales.mini_list.aum_label' })}</span>
                      </span>
                    </div>
                    <div className="text-sm font-semibold text-succession-text-primary">{formatCurrency(l.assets_under_management)}</div>
                  </div>
                  {/* Col 4: T12 */}
                  <div className="col-span-2 text-right">
                    <div className="text-xs text-succession-text-secondary mb-0.5">
                      <span className="inline-flex items-center gap-1 justify-end">
                        <svg className="w-3 h-3 text-green-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M3 17l6-6 4 4 7-7" />
                        </svg>
                        <span>{intl.formatMessage({ id: 'succession.partial_sales.mini_list.t12_label' })}</span>
                      </span>
                    </div>
                    <div className="text-sm font-semibold text-succession-text-primary">{formatCurrency(production)}</div>
                  </div>
                  {/* Col 5: Clients */}
                  <div className="col-span-2 text-right">
                    <div className="text-xs text-succession-text-secondary mb-0.5">
                      <span className="inline-flex items-center gap-1 justify-end">
                        <svg className="w-3 h-3 text-purple-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M16 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
                          <circle cx="9" cy="7" r="4" />
                          <path d="M22 21v-2a4 4 0 00-3-3.87" />
                          <path d="M16 3.13a4 4 0 010 7.75" />
                        </svg>
                        <span>{intl.formatMessage({ id: 'succession.partial_sales.mini_list.clients_label' })}</span>
                      </span>
                    </div>
                    <div className="text-sm font-semibold text-succession-text-primary">{l.number_of_clients || 0}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <div className="mt-3 pt-2 flex justify-center mt-auto">
        <a href="/succession/partial_sales" className="text-sm text-succession-primary hover:text-succession-primary-hover">{intl.formatMessage({ id: 'succession.partial_sales.mini_list.view_all' })}</a>
      </div>
    </div>
  );
};

export default PartialSalesMiniList;


